import { useCallback, useEffect, useRef, useId } from 'react';
import * as Sentry from '@sentry/browser';
import axios from 'axios';
import { toast } from '../components/ToastContainer';
import useLoadingState from './useLoadingState';
import ApiError from '../util/ApiError';
import useEventCallback from './useEventCallback';
import { getErrorMessage } from '../util/errors';

const defaultOpts = {
  /**
   * Pass `true` to display the error message,
   * or pass a string with a custom message.
   * You can also pass a function `error => true | string | null`
   */
  errorToast: true,
  /**
   * Pass a string with a custom message,
   * or pass a function `data => string | null`
   */
  successToast: null,
  /**
   * Optional toast id to explicitly dedupe toasts. If omitted, each success will create
   * a new toast (no deduplication).
   */
  toastId: undefined,
};

function usePush(endpoint, opts = {}) {
  const id = useId();
  const options = {
    ...defaultOpts,
    ...opts,
  };
  const { successToast, errorToast, toastId } = options;

  const {
    response,
    success,
    loading,
    error,
    dispatchLoad,
    dispatchError,
    dispatchSuccess,
    dispatchReset,
  } = useLoadingState();
  const mounted = useRef(true);
  const inFlight = useRef();

  const execute = useEventCallback((...args) => {
    dispatchLoad();
    const thenable = endpoint(...args);
    inFlight.current = thenable;
    return thenable
      .then(data => {
        if (thenable === inFlight.current && mounted.current) {
          dispatchSuccess(data);
        }
        const toastMessage = typeof successToast === 'function' ? successToast(data) : successToast;
        if (toastMessage) {
          // Only pass toastId when caller explicitly wants deduplication.
          const toastOptions = toastId ? { toastId } : {};
          toast.success(toastMessage, toastOptions);
        }
        return [undefined, data, thenable !== inFlight.current];
      })
      .catch(err => {
        const errorMessage = getErrorMessage(err);
        if (thenable === inFlight.current && mounted.current) {
          dispatchError(errorMessage);
        }
        const toastMessage = typeof errorToast === 'function' ? errorToast(err) : errorToast;
        if (toastMessage) {
          let message, options;
          if (toastMessage === true) {
            message = errorMessage;
          } else if (typeof toastMessage === 'string') {
            message = toastMessage;
          } else {
            ({ message, ...options } = toastMessage);
          }

          toast.error(message, { toastId: id, ...(options || {}) });
        }
        if (
          import.meta.env.VITE_ENV !== 'test' &&
          !(err instanceof ApiError) &&
          !axios.isCancel(err) &&
          !err.__sentryReported
        ) {
          console.error(err);
          Sentry.withScope(scope => {
            if (err.info) scope.setExtras(err.info);
            Sentry.captureException(err);
          });
        }
        return [err, undefined, thenable !== inFlight.current];
      });
  });

  const reset = useCallback(() => {
    inFlight.current = null;
    dispatchReset();
  }, [dispatchReset]);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  return {
    response,
    loading,
    error,
    success,
    execute,
    reset,
  };
}

export default usePush;
